import { useSyncExternalStore } from "react";
import type { DelegateTimelineEntry, PublicProgressIdentity, ToolCall } from "./types";
import type { TimelineScope } from "./delegateTimeline";

const ID=/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/;
const SEGMENT=/^cpu_[0-9a-f-]{36}$/;
export function validPublicProgress(value:unknown,companyId?:string):PublicProgressIdentity|undefined {
  const v=value as PublicProgressIdentity|undefined;
  if(v?.source!=="confirmed-claude-public-progress" ||
    ![v.companyId,v.requestMessageId,v.runId,v.segmentId].every(item=>typeof item==="string") || !ID.test(v.companyId??"") ||
    !ID.test(v.requestMessageId??"") || !/^cpu_[a-f0-9]{64}$/.test(v.runId??"") ||
    !SEGMENT.test(v.segmentId??"") || companyId!==undefined && v.companyId!==companyId) return;
  return {source:v.source,companyId:v.companyId,requestMessageId:v.requestMessageId,runId:v.runId,segmentId:v.segmentId};
}
const key=(v:Pick<PublicProgressIdentity,"companyId"|"requestMessageId"|"runId"|"segmentId">)=>
  JSON.stringify([v.companyId,v.requestMessageId,v.runId,v.segmentId]);
// Positive display proof only, bounded to this page lifetime. Never a success or
// execution receipt. Reload repopulates it from authenticated archive pages.
const segments=new Map<string,true>(),listeners=new Set<()=>void>();
const MAX_SEGMENTS=2048;
const subscribe=(listener:()=>void)=>{listeners.add(listener);return()=>{listeners.delete(listener);};};
const noSubscribe=()=>()=>{};
export function recordAuthenticatedPublicSegments(scope:TimelineScope,entries:DelegateTimelineEntry[]) {
  if(scope.provider!=="claude")return;
  let changed=false;
  for(const entry of entries){
    if(!Number.isSafeInteger(entry.sequence) || !entry.id || !["commentary","image"].includes(entry.type??""))continue;
    const proof=validPublicProgress({source:"confirmed-claude-public-progress",...scope,segmentId:entry.segmentId});
    if(!proof)continue;
    const k=key(proof);if(!segments.has(k)){segments.set(k,true);changed=true;}
  }
  while(segments.size>MAX_SEGMENTS){segments.delete(segments.keys().next().value!);changed=true;}
  if(changed)for(const listener of listeners)listener();
}
export function hasArchivedPublicProgress(tool:ToolCall,companyId:string|null,requestMessageId?:string) {
  const proof=validPublicProgress(tool.publicProgress,companyId??"");
  return !!proof && proof.requestMessageId===requestMessageId && segments.has(key(proof));
}
export function useArchivedPublicProgress(tools:ToolCall[],companyId:string|null,requestMessageId?:string) {
  const hasCandidates=tools.some(tool=>!!validPublicProgress(tool.publicProgress,companyId??""));
  const snapshot=()=>JSON.stringify(tools.filter(tool=>hasArchivedPublicProgress(tool,companyId,requestMessageId)).map(tool=>tool.id));
  const ids=useSyncExternalStore(hasCandidates?subscribe:noSubscribe,snapshot,snapshot);
  return new Set<string>(JSON.parse(ids));
}
